import { GET_TAG_BY_NAME } from '@/shared/api/graphql/tags';
import type { PatientsData } from '@/shared/lib/types/patients';
import Button from '@/shared/ui/Button';

import type { TaggedAccountsResponse } from '@/shared/api/graphql/account-lists';
import { GET_TAGGED_ACCOUNTS } from '@/shared/api/graphql/account-lists';
import type { GetTagByNameResponse } from '@/shared/lib/types/tag';

import { TAG_STARRED } from '@/shared/lib/util/constants';
import { useStarredPatientStore } from '@/shared/model/StarredPatientStore';
import { useQuery } from '@apollo/client';
import { Star } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { BoxProps } from '@mui/material';
import { Badge, Box, IconButton, Menu, MenuItem } from '@mui/material';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const MAX_ITEMS = 9;
const DEFAULT_PAGE_SIZE = 12;

type Props = BoxProps & { iconOnly?: boolean };
const StarredPatientsList = ({ iconOnly = false, ...props }: Props) => {
  const [starredAccounts, setStarredAccounts] = useState<Partial<PatientsData>[]>([]);
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchTagIds, setSearchTagIds] = useState<string[]>([]);
  const open = Boolean(anchorEl);

  const { setStarredPatients: setStarredAccountsStore, starredPatients: starredPatientsStore } = useStarredPatientStore();

  // First, get the "Starred" tag by name
  const { data: starredTagData } = useQuery<GetTagByNameResponse>(GET_TAG_BY_NAME, {
    variables: { tagName: TAG_STARRED },
    fetchPolicy: 'cache-first'
  });

  // Then, get all accounts with the "Starred" tag
  const { data: accountsByTags } = useQuery<TaggedAccountsResponse>(GET_TAGGED_ACCOUNTS, {
    variables: {
      pageNum: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      tagIds: searchTagIds
    },
    fetchPolicy: 'cache-first',
    skip: !searchTagIds || (Array.isArray(searchTagIds) && searchTagIds.length === 0)
  });

  // set the starred patients Zustand store based on the changes of the query response
  useEffect(() => {
    const accountsData: PatientsData[] = accountsByTags?.getTaggedAccounts?.accounts as PatientsData[];
    if (Array.isArray(accountsData)) {
      setStarredAccountsStore(accountsData);
      setStarredAccounts(accountsData);
    }
  }, [accountsByTags, setStarredAccountsStore]);

  // Change the local starred accounts based on the store changes
  useEffect(() => {
    setStarredAccounts(starredPatientsStore);
  }, [starredPatientsStore]);

  // Effect to set searchTagIds when starred tag is available
  useEffect(() => {
    if (starredTagData?.getTagByName?.id) {
      setSearchTagIds([starredTagData.getTagByName.id]);
    }
  }, [starredTagData]);

  // Effect to update starred accounts when data is received
  useEffect(() => {
    if (accountsByTags?.getTaggedAccounts?.accounts) {
      setStarredAccounts(accountsByTags.getTaggedAccounts.accounts as Partial<PatientsData>[]);
    }
  }, [accountsByTags]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (e: { target: { value: string | undefined } }) => {
    const redirectUrl = `patients/${e.target.value}/details/medications/triage`;
    navigate(redirectUrl);
    handleClose();
  };

  return (
    <Box {...props}>
      {starredTagData?.getTagByName && (
        <>
          {iconOnly ? (
            <IconButton color="inherit" size="small" onClick={handleClick} aria-haspopup="true" aria-expanded={open ? 'true' : undefined}>
              <Badge badgeContent={starredAccounts.length || 0} color="primary" max={9} invisible={starredAccounts.length === 0}>
                <Star />
              </Badge>
            </IconButton>
          ) : (
            <Button
              sx={{
                paddingY: 0.25,
                paddingX: 0.75
              }}
              size={'small'}
              variant={'outlined'}
              startIcon={<Star />}
              endIcon={<ExpandMoreIcon />}
              onClick={handleClick}
              label={'Starred'}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              disableElevation
            />
          )}
          <Menu
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            id="customized-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            slotProps={{
              paper: {
                sx: { maxHeight: '60vh', overflowY: 'auto' }
              },
              list: {
                'aria-labelledby': 'basic-button'
              }
            }}
          >
            {starredAccounts?.length === 0 && (
              <MenuItem disabled>Click stars next to patient names to add to this list for quick access</MenuItem>
            )}
            {starredAccounts?.slice(0, MAX_ITEMS).map(account => (
              <MenuItem
                key={account?.truentityId}
                value={account?.truentityId}
                onClick={() => handleChange({ target: { value: account?.truentityId } })}
              >
                {account?.user?.firstName} {account?.user?.lastName}
              </MenuItem>
            ))}
            {starredAccounts?.length > MAX_ITEMS && (
              <MenuItem key="view-more">
                <Link to={'/patients/tagged'}>View More</Link>
              </MenuItem>
            )}
          </Menu>
        </>
      )}
    </Box>
  );
};

export default StarredPatientsList;
